import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import {
  fetchFinanceCodes,
  getInvoiceFinanceCodeMappingState,
  normalizeFinanceCodes,
  saveFinanceCodes,
} from "../../services/financeCodes";

const QBO_TABS = {
  connection: "connection",
  defaults: "defaults",
  financeCodes: "financeCodes",
  taxMapping: "taxMapping",
  advanced: "advanced",
};
const ALLOWED_BILL_ACCOUNT_TYPES = new Set(["Expense", "Cost of Goods Sold", "Other Expense"]);

export default function QuickBooksSettings({ instanceUniqueId }) {
  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const [loading, setLoading] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resettingQbo, setResettingQbo] = useState(false);
  const [savingExportDefaults, setSavingExportDefaults] = useState(false);
  const [savingFinanceCodeMappings, setSavingFinanceCodeMappings] = useState(false);
  const [savingAdvancedJson, setSavingAdvancedJson] = useState(false);
  const [activeQboTab, setActiveQboTab] = useState(QBO_TABS.connection);
  const [status, setStatus] = useState({
    connected: false,
    realmId: null,
    env: null,
    suggestedEnv: null,
    modeMismatch: false,
    availableConnections: [],
    checking: false,
    qboCompanyCountryRaw: null,
    qboCompanyCountry: null,
    supportsMappedTaxCodes: false,
  });
  const [financeCodesLoading, setFinanceCodesLoading] = useState(false);
  const [financeCodes, setFinanceCodes] = useState([]);

  const [form, setForm] = useState({
    qbo_export_enabled: false,
    qbo_attach_pdf_on_export: true,
    qbo_queue_on_create_default: false,
    qbo_payment_auto_sync_enabled: false,
    qbo_env: "sandbox",
    invoice_item_ref: "",
    invoice_service_ref: "",
    invoice_tax_item_ref: "",
    bill_expense_ref: "",
    bill_cogs_ref: "",
    bill_freight_ref: "",
    bill_tax_expense_ref: "",
    invoice_tax_export_mode: "auto",
    invoice_deposit_mode: "consolidation",
    doc_prefix_invoice: "INV-",
    doc_prefix_bill: "BILL-",
    tax_map: "{}",
  });
  const [qboAccountsLoading, setQboAccountsLoading] = useState(false);
  const [qboAccounts, setQboAccounts] = useState([]);
  const [qboItemsLoading, setQboItemsLoading] = useState(false);
  const [qboItems, setQboItems] = useState([]);
  const [allQboItems, setAllQboItems] = useState([]);
  const [referenceData, setReferenceData] = useState({
    connected: false,
    env: null,
    realmId: null,
    savedReferenceEnv: null,
    savedReferenceRealmId: null,
    realmMismatch: false,
    qboWarning: null,
  });
  const [eliteTaxes, setEliteTaxes] = useState([]);
  const [qboTaxCodes, setQboTaxCodes] = useState([]);
  const [taxMapByTaxId, setTaxMapByTaxId] = useState({});
  const [taxMappingWarnings, setTaxMappingWarnings] = useState([]);
  const [taxMappingErrors, setTaxMappingErrors] = useState([]);
  const [loadingTaxMapping, setLoadingTaxMapping] = useState(false);
  const [savingTaxMapping, setSavingTaxMapping] = useState(false);

  const normalizeTaxMapFromSource = (rawMap = {}) => ({
    ...(rawMap && typeof rawMap === "object" ? rawMap : {}),
    byTaxId: rawMap?.byTaxId && typeof rawMap.byTaxId === "object" ? rawMap.byTaxId : {},
    __attach_pdf_on_export: rawMap?.__attach_pdf_on_export !== false,
  });

  const loadReferenceData = async (envOverride = null) => {
    if (!instanceUniqueId) return;
    setQboAccountsLoading(true);
    setQboItemsLoading(true);
    try {
      const res = await axios.get(`${SERVER}/invoices/qbo/reference-data`, {
        ...headers,
        params: { instanceUniqueId, env: envOverride || form.qbo_env || undefined },
      });
      setQboAccounts(Array.isArray(res?.data?.accounts) ? res.data.accounts : []);
      setQboItems(Array.isArray(res?.data?.items) ? res.data.items : []);
      setAllQboItems(Array.isArray(res?.data?.allItems) ? res.data.allItems : (Array.isArray(res?.data?.items) ? res.data.items : []));
      setReferenceData({
        connected: !!res?.data?.connected,
        env: res?.data?.env || null,
        realmId: res?.data?.realmId || null,
        savedReferenceEnv: res?.data?.savedReferenceEnv || null,
        savedReferenceRealmId: res?.data?.savedReferenceRealmId || null,
        realmMismatch: !!res?.data?.realmMismatch,
        qboWarning: res?.data?.qboWarning || null,
      });
    } catch {
      setQboAccounts([]);
      setQboItems([]);
      setAllQboItems([]);
      setReferenceData({
        connected: false,
        env: envOverride || form.qbo_env || null,
        realmId: null,
        savedReferenceEnv: null,
        savedReferenceRealmId: null,
        realmMismatch: false,
        qboWarning: null,
      });
    } finally {
      setQboAccountsLoading(false);
      setQboItemsLoading(false);
    }
  };

  const loadTaxMappingSource = async (envOverride = null) => {
    if (!instanceUniqueId) return;
    setLoadingTaxMapping(true);
    try {
      const res = await axios.get(`${SERVER}/invoices/qbo/tax-mapping-source`, {
        ...headers,
        params: { instanceUniqueId, env: envOverride || form.qbo_env || undefined },
      });
      const currentMap = normalizeTaxMapFromSource(res?.data?.currentMap || {});
      setEliteTaxes(Array.isArray(res?.data?.eliteTaxes) ? res.data.eliteTaxes : []);
      setQboTaxCodes(Array.isArray(res?.data?.qboTaxCodes) ? res.data.qboTaxCodes : []);
      setTaxMapByTaxId(currentMap.byTaxId || {});
      setForm((currentForm) => ({ ...currentForm, tax_map: JSON.stringify(currentMap, null, 2) }));
      setTaxMappingErrors([]);
      setTaxMappingWarnings(res?.data?.qboWarning ? [{ message: res.data.qboWarning }] : []);
    } catch (e) {
      setTaxMappingErrors([{ message: e?.response?.data?.error || "Failed to load tax mapping source." }]);
    } finally {
      setLoadingTaxMapping(false);
    }
  };

  const loadStatus = async () => {
    setStatus((currentStatus) => ({ ...currentStatus, checking: true }));
    try {
      const res = await axios.get(`${SERVER}/invoices/qbo/status`, {
        ...headers,
        params: { instanceUniqueId, env: form.qbo_env || undefined },
      });
      setStatus({
        connected: !!res.data.connected,
        realmId: res.data.realmId || null,
        env: res.data.env || null,
        suggestedEnv: res.data.suggestedEnv || null,
        modeMismatch: !!res.data.modeMismatch,
        availableConnections: Array.isArray(res.data.availableConnections) ? res.data.availableConnections : [],
        qboCompanyCountryRaw: res.data.qboCompanyCountryRaw || null,
        qboCompanyCountry: res.data.qboCompanyCountry || null,
        supportsMappedTaxCodes: !!res.data.supportsMappedTaxCodes,
        checking: false,
      });
    } catch {
      setStatus({
        connected: false,
        realmId: null,
        env: null,
        suggestedEnv: null,
        modeMismatch: false,
        availableConnections: [],
        checking: false,
        qboCompanyCountryRaw: null,
        qboCompanyCountry: null,
        supportsMappedTaxCodes: false,
      });
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${SERVER}/invoices/qbo/settings`, {
        ...headers,
        params: { instanceUniqueId, env: form.qbo_env || undefined },
      });
      const d = res.data || {};
      setForm({
        qbo_export_enabled: !!d.qbo_export_enabled,
        qbo_attach_pdf_on_export: d.qbo_attach_pdf_on_export !== false,
        qbo_queue_on_create_default: d.qbo_queue_on_create_default === true,
        qbo_payment_auto_sync_enabled: d.qbo_payment_auto_sync_enabled === true,
        qbo_env: d.qbo_env || "sandbox",
        invoice_item_ref: d.invoice_item_ref || "",
        invoice_service_ref: d.invoice_service_ref || "",
        invoice_tax_item_ref: d.invoice_tax_item_ref || "",
        bill_expense_ref: d.bill_expense_ref || "",
        bill_cogs_ref: d.bill_cogs_ref || "",
        bill_freight_ref: d.bill_freight_ref || "",
        bill_tax_expense_ref: d.bill_tax_expense_ref || "",
        invoice_tax_export_mode: d.invoice_tax_export_mode || "auto",
        invoice_deposit_mode: d.invoice_deposit_mode || "consolidation",
        doc_prefix_invoice: d.doc_prefix_invoice || "INV-",
        doc_prefix_bill: d.doc_prefix_bill || "BILL-",
        tax_map: JSON.stringify(d.tax_map || {}, null, 2),
      });
    } catch {
      alert("Failed to load QuickBooks settings.");
    } finally {
      setLoading(false);
    }
  };

  const loadFinanceCodes = async () => {
    if (!instanceUniqueId) return;
    setFinanceCodesLoading(true);
    try {
      const rows = await fetchFinanceCodes(instanceUniqueId, "all");
      setFinanceCodes(normalizeFinanceCodes(rows));
    } catch {
      setFinanceCodes([]);
    } finally {
      setFinanceCodesLoading(false);
    }
  };

  const refreshConnectionState = async () => {
    await Promise.all([
      loadStatus(),
      loadReferenceData(),
      loadTaxMappingSource(),
      loadFinanceCodes(),
    ]);
  };

  const qboTaxCodeLabel = (code) => {
    if (!code) return "";
    const description = code.description ? ` - ${code.description}` : "";
    return `${code.name || "Unnamed"} - ID ${code.id}${description}`;
  };

  const qboItemLabel = (item) => {
    if (!item) return "";
    const name = item.fullyQualifiedName || item.name || "Unnamed";
    const type = item.type || "Unknown";
    return `${name} - ${type} - ID ${item.id}`;
  };

  const qboAccountLabel = (account) => {
    if (!account) return "";
    const name = account.fullyQualifiedName || account.name || "Unnamed";
    return `${name} - ${account.accountType || "Unknown"} / ${account.accountSubType || "N/A"} - ID ${account.id}`;
  };

  const isQboCategoryItem = (item) => String(item?.type || "").trim().toLowerCase() === "category";
  const findById = (options, value) => options.find((option) => String(option.id) === String(value || "")) || null;

  const allItemIds = useMemo(() => new Set(allQboItems.map((item) => String(item.id))), [allQboItems]);
  const usableQboItems = useMemo(() => qboItems.filter((item) => !isQboCategoryItem(item)), [qboItems]);
  const accountIds = useMemo(() => new Set(qboAccounts.map((account) => String(account.id))), [qboAccounts]);
  const expenseAccounts = useMemo(
    () => qboAccounts.filter((account) => ALLOWED_BILL_ACCOUNT_TYPES.has(account.accountType)),
    [qboAccounts]
  );
  const vendorBillTaxAccounts = useMemo(
    () => qboAccounts.filter((account) => ["Expense", "Cost of Goods Sold", "Other Expense", "Other Current Liability", "Current Liability", "Long Term Liability"].includes(account.accountType)),
    [qboAccounts]
  );

  const selectedInvoiceItem = findById(allQboItems, form.invoice_item_ref);
  const selectedInvoiceServiceItem = findById(allQboItems, form.invoice_service_ref);
  const selectedInvoiceTaxItem = findById(allQboItems, form.invoice_tax_item_ref);
  const selectedBillExpenseAccount = findById(qboAccounts, form.bill_expense_ref);
  const selectedBillCogsAccount = findById(qboAccounts, form.bill_cogs_ref);
  const selectedBillFreightAccount = findById(qboAccounts, form.bill_freight_ref);
  const selectedBillTaxExpenseAccount = findById(qboAccounts, form.bill_tax_expense_ref);

  const qboRegionLabel = status.connected
    ? (status.qboCompanyCountryRaw || (status.qboCompanyCountry === "us" ? "US" : status.qboCompanyCountry === "non_us" ? "Non-US" : "unknown"))
    : "unknown";
  const supportsMappedTaxCodes = !!(status.connected && status.supportsMappedTaxCodes);
  const selectedNonUsWithoutSupport = form.invoice_tax_export_mode === "non_us" && !supportsMappedTaxCodes;
  const showTaxMapping = form.invoice_tax_export_mode === "non_us";

  const invoiceTaxModeHelperText = (() => {
    if (supportsMappedTaxCodes) {
      return "This connected QuickBooks company supports mapped QBO tax codes. Use Tax Mapping to map each Elite tax to the corresponding QBO sales/purchase tax code.";
    }
    if (status.connected && status.qboCompanyCountry === "us") {
      return "This connected QuickBooks company is US-based, so mapped Canadian/non-US tax codes are not available. Use Universal / Auto or Tax as invoice lines.";
    }
    if (!status.connected) {
      return "Connect QuickBooks before enabling Canada / Non-US mapped QBO tax code mode. Universal / Auto and Tax as invoice lines remain available.";
    }
    return "QuickBooks region is unknown, so Canada / Non-US mapped QBO tax code mode cannot be selected here. Universal / Auto and Tax as invoice lines remain available.";
  })();

  const getStaleFieldMessage = (value, type) => {
    if (!value || !referenceData.connected) return "";
    const exists = type === "item" ? allItemIds.has(String(value)) : accountIds.has(String(value));
    if (exists) return "";
    return `Saved QBO ref ID ${value} was not found in the connected ${referenceData.env || form.qbo_env || "selected"} realm. Please choose a new value.`;
  };

  const getCategoryFieldMessage = (value, label) => {
    if (!value || !referenceData.connected) return "";
    const item = findById(allQboItems, value);
    if (!item || !isQboCategoryItem(item)) return "";
    return `${label} saved QBO ref ID ${value} is a Category and cannot be used on invoices. Please choose a Product/Service item.`;
  };

  const referenceWarnings = [
    ...(referenceData.qboWarning ? [referenceData.qboWarning] : []),
    ...(referenceData.realmMismatch ? ["These saved refs were selected for a different QBO realm. Please reselect items and accounts."] : []),
    getStaleFieldMessage(form.invoice_item_ref, "item"),
    getStaleFieldMessage(form.invoice_service_ref, "item"),
    getStaleFieldMessage(form.invoice_tax_item_ref, "item"),
    getCategoryFieldMessage(form.invoice_item_ref, "Invoice Item Ref"),
    getCategoryFieldMessage(form.invoice_service_ref, "Invoice Service Ref"),
    getCategoryFieldMessage(form.invoice_tax_item_ref, "Invoice tax line item"),
    getStaleFieldMessage(form.bill_expense_ref, "account"),
    getStaleFieldMessage(form.bill_cogs_ref, "account"),
    getStaleFieldMessage(form.bill_freight_ref, "account"),
    getStaleFieldMessage(form.bill_tax_expense_ref, "account"),
  ].filter(Boolean);

  const statusForTaxRow = (tax) => {
    const key = String(tax.taxId);
    const mapping = taxMapByTaxId[key] || {};
    const salesRef = mapping.qboSalesTaxCodeRef || "";
    const purchaseRef = mapping.qboPurchaseTaxCodeRef || "";
    const rowErrors = taxMappingErrors.filter((item) => String(item.taxId || "") === key);
    const rowWarnings = taxMappingWarnings.filter((item) => String(item.taxId || "") === key);
    const codeIds = new Set(qboTaxCodes.map((code) => String(code.id)));
    const stale = [salesRef, purchaseRef].some((ref) => ref && !["TAX", "NON"].includes(String(ref).toUpperCase()) && qboTaxCodes.length && !codeIds.has(String(ref)));
    const salesCode = qboTaxCodes.find((code) => String(code.id) === String(salesRef));
    const isExempt = Number(tax.rate || 0) === 0;
    const exemptMapped = isExempt && (String(salesRef).toUpperCase() === "NON" || /NON|EXEMPT|OUT OF SCOPE|OOS|ZERO/i.test(salesCode?.name || ""));

    if (rowErrors.length) return { label: "Missing", color: "error" };
    if (rowWarnings.length || stale || String(salesRef).toUpperCase() === "TAX") return { label: "Warning", color: "warning" };
    if (exemptMapped) return { label: "Exempt", color: "default" };
    if (salesRef && purchaseRef) return { label: "Mapped", color: "success" };
    return { label: "Missing", color: "error" };
  };

  const updateTaxMapRow = (tax, patch) => {
    setTaxMapByTaxId((prev) => {
      const key = String(tax.taxId);
      return {
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          eliteTaxId: tax.taxId,
          eliteCode: tax.canonicalKey,
          eliteLabel: tax.label,
          countryCode: tax.countryCode,
          stateCode: tax.stateCode,
          rate: tax.rate,
          ...patch,
        },
      };
    });
  };

  const persistTaxMapping = async () => {
    const byTaxId = {};
    eliteTaxes.forEach((tax) => {
      const existing = taxMapByTaxId[String(tax.taxId)] || {};
      byTaxId[String(tax.taxId)] = {
        ...existing,
        eliteTaxId: tax.taxId,
        eliteCode: tax.canonicalKey,
        eliteLabel: tax.label,
        countryCode: tax.countryCode,
        stateCode: tax.stateCode,
        rate: tax.rate,
      };
    });
    const res = await axios.put(`${SERVER}/invoices/qbo/tax-map`, { instanceUniqueId, byTaxId }, headers);
    const nextMap = normalizeTaxMapFromSource(res?.data?.tax_map || {});
    setTaxMapByTaxId(nextMap.byTaxId || {});
    setForm((currentForm) => ({ ...currentForm, tax_map: JSON.stringify(nextMap, null, 2) }));
    return nextMap;
  };

  const saveTaxMapping = async () => {
    setSavingTaxMapping(true);
    try {
      await persistTaxMapping();
      alert("Tax mapping saved.");
    } catch (e) {
      alert(e?.response?.data?.error || "Failed to save tax mapping.");
    } finally {
      setSavingTaxMapping(false);
    }
  };

  const autoMatchTaxMapping = async () => {
    setSavingTaxMapping(true);
    try {
      const res = await axios.post(`${SERVER}/invoices/qbo/tax-map/auto-match`, {
        instanceUniqueId,
        env: form.qbo_env || undefined,
        confirmSave: false,
      }, headers);
      const suggestions = res?.data?.suggestions || {};
      setTaxMapByTaxId((prev) => ({ ...prev, ...suggestions }));
      setTaxMappingErrors([]);
      setTaxMappingWarnings(res?.data?.qboWarning ? [{ message: res.data.qboWarning }] : []);
    } catch (e) {
      setTaxMappingErrors([{ message: e?.response?.data?.error || "Failed to auto-match tax mapping." }]);
    } finally {
      setSavingTaxMapping(false);
    }
  };

  const validateTaxMapping = async () => {
    setLoadingTaxMapping(true);
    try {
      await persistTaxMapping();
      const res = await axios.post(`${SERVER}/invoices/qbo/tax-map/validate`, {
        instanceUniqueId,
        env: form.qbo_env || undefined,
      }, headers);
      setTaxMappingErrors(Array.isArray(res?.data?.errors) ? res.data.errors : []);
      const warnings = Array.isArray(res?.data?.warnings) ? res.data.warnings : [];
      setTaxMappingWarnings(res?.data?.qboWarning ? [{ message: res.data.qboWarning }, ...warnings] : warnings);
    } catch (e) {
      setTaxMappingErrors([{ message: e?.response?.data?.error || "Failed to validate tax mapping." }]);
    } finally {
      setLoadingTaxMapping(false);
    }
  };

  const rateLabel = (rate) => {
    const num = Number(rate || 0);
    const pct = Math.abs(num) <= 1 ? num * 100 : num;
    return `${pct.toFixed(3).replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1")}%`;
  };

  const setField = (key) => (event) => setForm((currentForm) => ({ ...currentForm, [key]: event.target.value }));

  const startConnect = async () => {
    try {
      const res = await axios.get(`${SERVER}/invoices/qbo/connect`, {
        ...headers,
        params: { instanceUniqueId, env: form.qbo_env || undefined },
      });
      const url = res.data?.url;
      if (!url) {
        alert("Server did not return the QuickBooks OAuth URL.");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      alert(e?.response?.data?.error || "Could not start QuickBooks connection.");
    }
  };

  const switchQboMode = async (nextEnv) => {
    const env = String(nextEnv || "").toLowerCase();
    if (!["sandbox", "production"].includes(env)) return;

    setForm((currentForm) => ({ ...currentForm, qbo_env: env }));
    try {
      await axios.put(`${SERVER}/invoices/qbo/settings`, {
        instanceUniqueId,
        qbo_env: env,
      }, headers);
      await loadStatus();
      await loadReferenceData(env);
      await loadTaxMappingSource(env);
    } catch (e) {
      alert(e?.response?.data?.error || "Could not switch QuickBooks environment.");
    }
  };

  const resetQboIntegration = async () => {
    const env = form.qbo_env || "sandbox";
    setResettingQbo(true);
    try {
      await axios.post(`${SERVER}/invoices/qbo/reset`, {
        instanceUniqueId,
        env,
        clearSettings: true,
      }, headers);

      setResetConfirmOpen(false);
      await Promise.all([
        loadStatus(),
        loadSettings(),
        loadReferenceData(env),
        loadTaxMappingSource(env),
        loadFinanceCodes(),
      ]);
      alert("QuickBooks integration reset. Reconnect and remap QuickBooks before exporting again.");
    } catch (e) {
      alert(e?.response?.data?.error || "Failed to reset QuickBooks integration.");
    } finally {
      setResettingQbo(false);
    }
  };

  const validateExportDefaults = () => {
    let parsedTax = {};
    try {
      parsedTax = JSON.parse(form.tax_map || "{}");
    } catch {
      throw new Error("Tax map must be valid JSON.");
    }

    const usableItemIds = new Set(usableQboItems.map((item) => String(item.id)));
    const refExists = (value, validIds) => !value || !referenceData.connected || validIds.has(String(value));
    const refIsUsableItem = (value) => !value || !referenceData.connected || usableItemIds.has(String(value));

    if (!refIsUsableItem(form.invoice_item_ref)) {
      throw new Error("Choose a valid QuickBooks Product/Service item. Categories cannot be used for invoice exports.");
    }
    if (!refIsUsableItem(form.invoice_service_ref)) {
      throw new Error("Choose a valid QuickBooks Product/Service item. Categories cannot be used for invoice exports.");
    }
    if (!refIsUsableItem(form.invoice_tax_item_ref)) {
      throw new Error("Choose a valid QuickBooks Product/Service item. Categories cannot be used for invoice exports.");
    }
    if (!refExists(form.bill_expense_ref, accountIds)) {
      throw new Error("Choose a valid QuickBooks account for Bill Expense Account Ref.");
    }
    if (!refExists(form.bill_cogs_ref, accountIds)) {
      throw new Error("Choose a valid QuickBooks account for Default Bill Expense / COGS Account.");
    }
    if (!refExists(form.bill_freight_ref, accountIds)) {
      throw new Error("Choose a valid QuickBooks account for Bill Freight Account Ref.");
    }
    if (!refExists(form.bill_tax_expense_ref, accountIds)) {
      throw new Error("Choose a valid QuickBooks account for Vendor bill tax account.");
    }
    if (form.invoice_tax_export_mode === "non_us" && !supportsMappedTaxCodes) {
      throw new Error("Mapped QBO tax code export is only available for Canada/non-US QuickBooks companies. Use Universal / Auto or Tax as invoice lines.");
    }

    return parsedTax;
  };

  const buildNormalizedFinanceCodes = () => normalizeFinanceCodes(financeCodes).map((row) => {
    const selectedInvoiceOption = findById(allQboItems, row.qboInvoiceItemRef);
    const selectedBillAccount = findById(qboAccounts, row.qboBillAccountRef);

    if (referenceData.connected && row.qboInvoiceItemRef && selectedInvoiceOption && isQboCategoryItem(selectedInvoiceOption)) {
      throw new Error(`Finance code "${row.code}" is mapped to a QuickBooks Category item. Choose a Product/Service item instead.`);
    }
    if (referenceData.connected && row.qboBillAccountRef && selectedBillAccount && !ALLOWED_BILL_ACCOUNT_TYPES.has(String(selectedBillAccount.accountType || ""))) {
      throw new Error(`Finance code "${row.code}" is mapped to an unsupported QuickBooks bill account type.`);
    }

    return {
      ...row,
      qboInvoiceItemRef: row.qboInvoiceItemRef || "",
      qboInvoiceItemName: selectedInvoiceOption?.fullyQualifiedName || selectedInvoiceOption?.name || row.qboInvoiceItemName || "",
      qboInvoiceItemType: selectedInvoiceOption?.type || row.qboInvoiceItemType || "",
      qboBillAccountRef: row.qboBillAccountRef || "",
      qboBillAccountName: selectedBillAccount?.fullyQualifiedName || selectedBillAccount?.name || row.qboBillAccountName || "",
      qboBillAccountType: selectedBillAccount?.accountType || row.qboBillAccountType || "",
      qboBillAccountSubType: selectedBillAccount?.accountSubType || row.qboBillAccountSubType || "",
    };
  });

  const saveExportDefaults = async () => {
    setSavingExportDefaults(true);
    try {
      const parsedTax = validateExportDefaults();
      await axios.put(`${SERVER}/invoices/qbo/settings`, {
        instanceUniqueId,
        qbo_export_enabled: form.qbo_export_enabled,
        qbo_attach_pdf_on_export: form.qbo_attach_pdf_on_export,
        qbo_queue_on_create_default: form.qbo_queue_on_create_default,
        qbo_payment_auto_sync_enabled: form.qbo_payment_auto_sync_enabled,
        qbo_env: form.qbo_env,
        invoice_item_ref: form.invoice_item_ref,
        invoice_service_ref: form.invoice_service_ref,
        invoice_tax_item_ref: form.invoice_tax_item_ref,
        bill_expense_ref: form.bill_expense_ref,
        bill_cogs_ref: form.bill_cogs_ref,
        bill_freight_ref: form.bill_freight_ref,
        bill_tax_expense_ref: form.bill_tax_expense_ref,
        invoice_tax_export_mode: form.invoice_tax_export_mode,
        invoice_deposit_mode: form.invoice_deposit_mode || "consolidation",
        doc_prefix_invoice: form.doc_prefix_invoice,
        doc_prefix_bill: form.doc_prefix_bill,
        tax_map: parsedTax,
      }, headers);
      await loadReferenceData(form.qbo_env);
      await loadSettings();
      alert(form.invoice_tax_item_ref ? "Export defaults saved." : "Export defaults saved. Taxable invoice exports will fail until Invoice tax line item is configured.");
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || "Failed to save export defaults.");
    } finally {
      setSavingExportDefaults(false);
    }
  };

  const saveFinanceCodeMappings = async () => {
    setSavingFinanceCodeMappings(true);
    try {
      const normalizedRows = buildNormalizedFinanceCodes();
      await saveFinanceCodes(instanceUniqueId, normalizedRows);
      await loadFinanceCodes();
      alert("Finance code mapping saved.");
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || "Failed to save finance code mappings.");
    } finally {
      setSavingFinanceCodeMappings(false);
    }
  };

  const saveAdvancedTaxJson = async () => {
    setSavingAdvancedJson(true);
    try {
      let parsedTax = {};
      try {
        parsedTax = JSON.parse(form.tax_map || "{}");
      } catch {
        throw new Error("Tax map must be valid JSON.");
      }
      await axios.put(`${SERVER}/invoices/qbo/settings`, {
        instanceUniqueId,
        tax_map: parsedTax,
      }, headers);
      await loadTaxMappingSource(form.qbo_env);
      await loadSettings();
      alert("Advanced tax JSON saved.");
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || "Failed to save advanced JSON.");
    } finally {
      setSavingAdvancedJson(false);
    }
  };

  const financeCodeRows = normalizeFinanceCodes(financeCodes);
  const createStaleInvoiceItemOption = (row = {}) => row?.qboInvoiceItemRef
    ? {
        id: String(row.qboInvoiceItemRef),
        name: row.qboInvoiceItemName || `Saved item ${row.qboInvoiceItemRef}`,
        fullyQualifiedName: row.qboInvoiceItemName || `Saved item ${row.qboInvoiceItemRef}`,
        type: row.qboInvoiceItemType || "Missing",
      }
    : null;
  const createStaleAccountOption = (row = {}) => row?.qboBillAccountRef
    ? {
        id: String(row.qboBillAccountRef),
        name: row.qboBillAccountName || `Saved account ${row.qboBillAccountRef}`,
        fullyQualifiedName: row.qboBillAccountName || `Saved account ${row.qboBillAccountRef}`,
        accountType: row.qboBillAccountType || "Missing",
        accountSubType: row.qboBillAccountSubType || "Missing",
      }
    : null;

  const updateFinanceCodeRow = (rowId, patch) => {
    setFinanceCodes((currentRows) => normalizeFinanceCodes(currentRows.map((row) => (
      String(row?.id) === String(rowId)
        ? { ...row, ...patch, updatedAt: new Date().toISOString() }
        : row
    ))));
  };

  const getFinanceCodeInvoiceWarning = (row) => {
    if (!row?.qboInvoiceItemRef || !referenceData.connected) return "";
    const item = findById(allQboItems, row.qboInvoiceItemRef);
    if (!item) return `Saved invoice item ref ${row.qboInvoiceItemRef} was not found in the connected realm.`;
    if (isQboCategoryItem(item)) return `Saved invoice item ref ${row.qboInvoiceItemRef} is a QBO Category and cannot be used for invoice exports.`;
    return "";
  };

  const getFinanceCodeBillWarning = (row) => {
    if (!row?.qboBillAccountRef || !referenceData.connected) return "";
    const account = findById(qboAccounts, row.qboBillAccountRef);
    if (!account) return `Saved bill account ref ${row.qboBillAccountRef} was not found in the connected realm.`;
    if (!ALLOWED_BILL_ACCOUNT_TYPES.has(String(account?.accountType || ""))) {
      return `Saved bill account ref ${row.qboBillAccountRef} is ${account?.accountType || "an unsupported type"} and cannot be used for bill expense routing.`;
    }
    return "";
  };

  const statusSummary = status.connected
    ? `Connected${status.realmId ? ` • Realm ${status.realmId}` : ""} • ${String(status.env || form.qbo_env || "sandbox").toUpperCase()} • Region ${qboRegionLabel}`
    : `Not connected • ${String(status.env || form.qbo_env || "sandbox").toUpperCase()} • Region ${qboRegionLabel}`;

  const connectionWarnings = [
    ...(status.modeMismatch && status.suggestedEnv
      ? [`Connection exists in ${String(status.suggestedEnv).toUpperCase()} mode. Switch environment below or reconnect.`]
      : []),
    ...(referenceData.qboWarning ? [referenceData.qboWarning] : []),
  ];

  const renderConnectionTab = () => (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        {status.checking ? (
          <CircularProgress size={18} />
        ) : status.connected ? (
          <Chip color="success" size="small" label={`Connected${status.realmId ? ` • Realm ${status.realmId}` : ""}`} />
        ) : (
          <Chip size="small" label="Not connected" />
        )}
        <Chip size="small" variant="outlined" label={`Mode ${String(status.env || form.qbo_env || "sandbox").toUpperCase()}`} />
        <Chip size="small" variant="outlined" label={`Region ${qboRegionLabel}`} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Current Connection</Typography>
          <Typography variant="body2" color="text.secondary">Realm ID: {status.realmId || "Not connected"}</Typography>
          <Typography variant="body2" color="text.secondary">Active mode: {String(status.env || form.qbo_env || "sandbox").toUpperCase()}</Typography>
          <Typography variant="body2" color="text.secondary">Region: {qboRegionLabel}</Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Available Connections</Typography>
          {Array.isArray(status.availableConnections) && status.availableConnections.length > 0 ? (
            status.availableConnections.map((connection, index) => {
              const region = connection?.qboCompanyCountryRaw || (connection?.qboCompanyCountry === "us" ? "US" : connection?.qboCompanyCountry === "non_us" ? "Non-US" : "unknown");
              return (
                <Typography key={`qbo-connection-${index}`} variant="body2" color="text.secondary">
                  {String(connection?.env || "unknown").toUpperCase()}
                  {connection?.realmId ? ` • Realm ${connection.realmId}` : ""}
                  {region ? ` • Region ${region}` : ""}
                </Typography>
              );
            })
          ) : (
            <Typography variant="body2" color="text.secondary">No saved QuickBooks connections found for this instance.</Typography>
          )}
        </Box>
      </Box>

      {connectionWarnings.length > 0 ? (
        <Alert severity="warning">
          {connectionWarnings.map((message, idx) => (
            <Typography key={`connection-warning-${idx}`} variant="caption" display="block">{message}</Typography>
          ))}
        </Alert>
      ) : null}

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button variant="outlined" onClick={refreshConnectionState} disabled={status.checking || qboAccountsLoading || qboItemsLoading || loadingTaxMapping}>
          {(status.checking || qboAccountsLoading || qboItemsLoading || loadingTaxMapping) ? "Refreshing..." : "Refresh"}
        </Button>
        {!status.connected ? (
          <Button variant="contained" onClick={startConnect}>Connect QuickBooks</Button>
        ) : null}
        <Button size="small" variant={form.qbo_env === "sandbox" ? "contained" : "outlined"} onClick={() => switchQboMode("sandbox")}>
          Use Sandbox
        </Button>
        <Button size="small" variant={form.qbo_env === "production" ? "contained" : "outlined"} onClick={() => switchQboMode("production")}>
          Use Production
        </Button>
        <Button variant="outlined" color="warning" onClick={() => setResetConfirmOpen(true)} disabled={loading || resettingQbo}>
          {resettingQbo ? "Resetting..." : "Reset QuickBooks Integration"}
        </Button>
      </Box>
    </Box>
  );

  const renderExportDefaultsTab = () => (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <FormControlLabel
          control={(
            <Switch
              checked={!!form.qbo_export_enabled}
              onChange={(e) => setForm((currentForm) => ({ ...currentForm, qbo_export_enabled: e.target.checked }))}
            />
          )}
          label="Enable QBO Exports"
        />
        <FormControlLabel
          control={(
            <Switch
              checked={form.qbo_attach_pdf_on_export !== false}
              onChange={(e) => setForm((currentForm) => ({ ...currentForm, qbo_attach_pdf_on_export: e.target.checked }))}
            />
          )}
          label="Generate PDF and attach to QuickBooks on export"
        />
      </Box>

      <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Invoice Sync Defaults
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Control whether newly created invoices are marked as pending for QuickBooks export.
        </Typography>
        <FormControlLabel
          control={(
            <Switch
              checked={form.qbo_queue_on_create_default === true}
              onChange={(e) => setForm((currentForm) => ({ ...currentForm, qbo_queue_on_create_default: e.target.checked }))}
            />
          )}
          label="Queue new invoices for QuickBooks export by default"
        />
        <Typography variant="caption" color="text.secondary" display="block">
          When enabled, newly created invoices are marked pending for QuickBooks export. This does not check payment status or create payments in QuickBooks.
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Payment Status Sync
        </Typography>
        <FormControlLabel
          control={(
            <Switch
              checked={form.qbo_payment_auto_sync_enabled === true}
              onChange={(e) => setForm((currentForm) => ({ ...currentForm, qbo_payment_auto_sync_enabled: e.target.checked }))}
            />
          )}
          label="Automatically check QuickBooks invoice payment status"
        />
        <Typography variant="caption" color="text.secondary" display="block">
          When enabled, Elite Core periodically checks QuickBooks Online for payment updates on invoices that were already exported to QuickBooks. This does not queue new invoices for export, does not export invoices, and does not create QuickBooks payments.
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Autocomplete
          options={usableQboItems}
          loading={qboItemsLoading}
          value={selectedInvoiceItem}
          disabled={!referenceData.connected}
          onChange={(_, value) => {
            setForm((currentForm) => ({ ...currentForm, invoice_item_ref: value ? String(value.id || "").trim() : "" }));
          }}
          getOptionLabel={qboItemLabel}
          isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Invoice Item Ref"
              helperText={!referenceData.connected
                ? "Connect QuickBooks to choose QBO items/accounts."
                : (getCategoryFieldMessage(form.invoice_item_ref, "Invoice Item Ref") || getStaleFieldMessage(form.invoice_item_ref, "item") || "Choose the default QuickBooks Product/Service item for invoice product lines.")}
            />
          )}
        />
        <Autocomplete
          options={usableQboItems}
          loading={qboItemsLoading}
          value={selectedInvoiceServiceItem}
          disabled={!referenceData.connected}
          onChange={(_, value) => {
            setForm((currentForm) => ({ ...currentForm, invoice_service_ref: value ? String(value.id || "").trim() : "" }));
          }}
          getOptionLabel={qboItemLabel}
          isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Invoice Service Ref"
              helperText={!referenceData.connected
                ? "Connect QuickBooks to choose QBO items/accounts."
                : (getCategoryFieldMessage(form.invoice_service_ref, "Invoice Service Ref") || getStaleFieldMessage(form.invoice_service_ref, "item") || "Choose the QuickBooks Product/Service item used when service-only invoice lines are exported.")}
            />
          )}
        />
        <Autocomplete
          options={usableQboItems}
          loading={qboItemsLoading}
          value={selectedInvoiceTaxItem}
          disabled={!referenceData.connected}
          onChange={(_, value) => {
            setForm((currentForm) => ({ ...currentForm, invoice_tax_item_ref: value ? String(value.id || "").trim() : "" }));
          }}
          getOptionLabel={qboItemLabel}
          isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Invoice tax line item"
              helperText={!referenceData.connected
                ? "Connect QuickBooks to choose QBO items/accounts."
                : (getCategoryFieldMessage(form.invoice_tax_item_ref, "Invoice tax line item") || getStaleFieldMessage(form.invoice_tax_item_ref, "item") || "Used when exporting taxes as separate invoice lines. Choose a QBO Product/Service item for tax collected or tax liability handling.")}
            />
          )}
        />
        <FormControl fullWidth>
          <InputLabel id="invoice-tax-export-mode-label">Invoice tax export mode</InputLabel>
          <Select
            labelId="invoice-tax-export-mode-label"
            label="Invoice tax export mode"
            value={form.invoice_tax_export_mode || "auto"}
            onChange={setField("invoice_tax_export_mode")}
          >
            <MenuItem value="auto">Universal / Auto</MenuItem>
            <MenuItem value="us" disabled>US - QBO TAX/NON</MenuItem>
            <MenuItem value="non_us" disabled={!supportsMappedTaxCodes}>Canada / Non-US - mapped QBO tax codes</MenuItem>
            <MenuItem value="tax_lines">Tax as invoice lines</MenuItem>
          </Select>
          <Typography variant="caption" color={selectedNonUsWithoutSupport ? "warning.main" : "text.secondary"} sx={{ mt: 0.5, ml: 1.75 }}>
            {selectedNonUsWithoutSupport
              ? "Saved mode is Canada / Non-US mapped QBO tax codes, but this connection is US-based, unknown, or not connected. Change modes before saving/exporting, or connect a Canada/non-US company."
              : invoiceTaxModeHelperText}
          </Typography>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="invoice-deposit-mode-label">Invoice deposit mode</InputLabel>
          <Select
            labelId="invoice-deposit-mode-label"
            label="Invoice deposit mode"
            value={form.invoice_deposit_mode || "consolidation"}
            onChange={setField("invoice_deposit_mode")}
          >
            <MenuItem value="consolidation">Deposit consolidation</MenuItem>
            <MenuItem value="separate" disabled title="Coming soon. This would allow deposit invoices to be shown/exported separately.">
              Show deposits separately
            </MenuItem>
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
            Deposit consolidation keeps deposit invoices inside Elite and exports the follow-up invoice as the QBO-facing invoice. Deposits are not exported separately in this mode.
          </Typography>
        </FormControl>
        <Autocomplete
          options={expenseAccounts}
          loading={qboAccountsLoading}
          value={selectedBillExpenseAccount}
          disabled={!referenceData.connected}
          onChange={(_, value) => {
            setForm((currentForm) => ({ ...currentForm, bill_expense_ref: value ? String(value.id || "").trim() : "" }));
          }}
          getOptionLabel={qboAccountLabel}
          isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Bill Expense Account Ref"
              helperText={!referenceData.connected
                ? "Connect QuickBooks to choose QBO items/accounts."
                : (getStaleFieldMessage(form.bill_expense_ref, "account") || "Choose the QuickBooks expense account used for vendor bill expense lines.")}
            />
          )}
        />
        <Autocomplete
          options={expenseAccounts}
          loading={qboAccountsLoading}
          value={selectedBillCogsAccount}
          disabled={!referenceData.connected}
          onChange={(_, value) => {
            setForm((currentForm) => ({ ...currentForm, bill_cogs_ref: value ? String(value.id || "").trim() : "" }));
          }}
          getOptionLabel={qboAccountLabel}
          isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Default Bill Expense / COGS Account"
              helperText={!referenceData.connected
                ? "Connect QuickBooks to choose QBO items/accounts."
                : (getStaleFieldMessage(form.bill_cogs_ref, "account") || "Used as the AccountRef for QuickBooks bill expense lines.")}
            />
          )}
        />
        <Autocomplete
          options={expenseAccounts}
          loading={qboAccountsLoading}
          value={selectedBillFreightAccount}
          disabled={!referenceData.connected}
          onChange={(_, value) => {
            setForm((currentForm) => ({ ...currentForm, bill_freight_ref: value ? String(value.id || "").trim() : "" }));
          }}
          getOptionLabel={qboAccountLabel}
          isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Bill Freight Account Ref"
              helperText={!referenceData.connected
                ? "Connect QuickBooks to choose QBO items/accounts."
                : (getStaleFieldMessage(form.bill_freight_ref, "account") || "Choose the QuickBooks freight/shipping expense account used for bill freight lines.")}
            />
          )}
        />
        <Autocomplete
          options={vendorBillTaxAccounts}
          loading={qboAccountsLoading}
          value={selectedBillTaxExpenseAccount}
          disabled={!referenceData.connected}
          onChange={(_, value) => {
            setForm((currentForm) => ({ ...currentForm, bill_tax_expense_ref: value ? String(value.id || "").trim() : "" }));
          }}
          getOptionLabel={qboAccountLabel}
          isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vendor bill tax account"
              helperText={!referenceData.connected
                ? "Connect QuickBooks to choose QBO items/accounts."
                : (getStaleFieldMessage(form.bill_tax_expense_ref, "account") || "Vendor bill taxes are exported as separate bill lines because QuickBooks does not support mapping specific tax rates to vendor bill lines in this company.")}
            />
          )}
        />
        <TextField label="Invoice Doc Prefix" value={form.doc_prefix_invoice} onChange={setField("doc_prefix_invoice")} />
        <TextField label="Bill Doc Prefix" value={form.doc_prefix_bill} onChange={setField("doc_prefix_bill")} />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={saveExportDefaults} disabled={savingExportDefaults || loading}>
          {savingExportDefaults ? "Saving..." : "Save Export Defaults"}
        </Button>
      </Box>
    </Box>
  );

  const renderFinanceCodeMappingTab = () => (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box>
        <Typography variant="h6">Finance Code Mapping</Typography>
        <Typography variant="body2" color="text.secondary">
          Map Elite finance codes to QuickBooks references. Invoice exports use Product/Service items. Bill exports use Expense/COGS accounts. Defaults below are used when a code has no mapping.
        </Typography>
      </Box>

      {!referenceData.connected ? (
        <Alert severity="info">Connect QuickBooks to choose QBO references.</Alert>
      ) : null}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Elite Code</TableCell>
            <TableCell>Status</TableCell>
            <TableCell sx={{ minWidth: 260 }}>QBO Invoice Item</TableCell>
            <TableCell sx={{ minWidth: 260 }}>QBO Bill Account</TableCell>
            <TableCell sx={{ minWidth: 220 }}>Warnings</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {financeCodeRows.length ? financeCodeRows.map((row) => {
            const selectedInvoiceOption = findById(usableQboItems, row.qboInvoiceItemRef)
              || findById(allQboItems, row.qboInvoiceItemRef)
              || createStaleInvoiceItemOption(row);
            const selectedBillAccountOption = findById(expenseAccounts, row.qboBillAccountRef)
              || findById(qboAccounts, row.qboBillAccountRef)
              || createStaleAccountOption(row);
            const invoiceMappingState = getInvoiceFinanceCodeMappingState(row);
            const invoiceWarning = getFinanceCodeInvoiceWarning(row);
            const billWarning = getFinanceCodeBillWarning(row);
            const warnings = [invoiceWarning, billWarning].filter(Boolean);

            return (
              <TableRow key={row.id || row.code}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.label || row.code}</Typography>
                  <Typography variant="caption" color="text.secondary">{row.code}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={row.enabled === false ? "default" : "success"}
                    label={row.enabled === false ? "Disabled" : "Enabled"}
                  />
                </TableCell>
                <TableCell>
                  <Autocomplete
                    options={usableQboItems}
                    loading={qboItemsLoading || financeCodesLoading}
                    value={selectedInvoiceOption}
                    disabled={!referenceData.connected || financeCodesLoading || invoiceMappingState.disabled}
                    onChange={(_, value) => {
                      updateFinanceCodeRow(row.id, {
                        qboInvoiceItemRef: value ? String(value.id || "").trim() : "",
                        qboInvoiceItemName: value?.fullyQualifiedName || value?.name || "",
                        qboInvoiceItemType: value?.type || "",
                      });
                    }}
                    getOptionLabel={qboItemLabel}
                    isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Invoice Item"
                        helperText={!referenceData.connected
                          ? "Connect QuickBooks to choose QBO references."
                          : (invoiceMappingState.helperText || invoiceWarning || "Blank uses the global invoice item defaults below.")}
                      />
                    )}
                  />
                </TableCell>
                <TableCell>
                  <Autocomplete
                    options={expenseAccounts}
                    loading={qboAccountsLoading || financeCodesLoading}
                    value={selectedBillAccountOption}
                    disabled={!referenceData.connected || financeCodesLoading}
                    onChange={(_, value) => {
                      updateFinanceCodeRow(row.id, {
                        qboBillAccountRef: value ? String(value.id || "").trim() : "",
                        qboBillAccountName: value?.fullyQualifiedName || value?.name || "",
                        qboBillAccountType: value?.accountType || "",
                        qboBillAccountSubType: value?.accountSubType || "",
                      });
                    }}
                    getOptionLabel={qboAccountLabel}
                    isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Bill Account"
                        helperText={!referenceData.connected
                          ? "Connect QuickBooks to choose QBO references."
                          : (billWarning || "Blank uses freight/default bill account fallbacks below.")}
                      />
                    )}
                  />
                </TableCell>
                <TableCell>
                  {warnings.length ? warnings.map((warning, idx) => (
                    <Typography key={`${row.id}-warning-${idx}`} variant="caption" display="block" color="warning.main">
                      {warning}
                    </Typography>
                  )) : (
                    <Typography variant="caption" color="text.secondary">
                      {row.enabled === false ? "Legacy mapping preserved for disabled code." : "No warnings"}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    onClick={() => updateFinanceCodeRow(row.id, {
                      qboInvoiceItemRef: "",
                      qboInvoiceItemName: "",
                      qboInvoiceItemType: "",
                      qboBillAccountRef: "",
                      qboBillAccountName: "",
                      qboBillAccountType: "",
                      qboBillAccountSubType: "",
                    })}
                    disabled={financeCodesLoading}
                  >
                    Clear
                  </Button>
                </TableCell>
              </TableRow>
            );
          }) : (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography variant="body2">
                  No finance codes are configured for this instance yet. Add them under Company Settings &gt; Defaults &gt; Finance Codes.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={saveFinanceCodeMappings} disabled={savingFinanceCodeMappings || financeCodesLoading}>
          {savingFinanceCodeMappings ? "Saving..." : "Save Finance Code Mapping"}
        </Button>
      </Box>
    </Box>
  );

  const renderTaxMappingTab = () => (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
        <Box>
          <Typography variant="h6">Tax Mapping</Typography>
          <Typography variant="body2" color="text.secondary">
            Map each Elite tax to separate QuickBooks sales and purchase tax codes. Use the advanced tab only for manual JSON overrides.
          </Typography>
        </Box>
        {loadingTaxMapping ? <CircularProgress size={20} /> : null}
      </Box>

      {!showTaxMapping ? (
        <Alert severity="info">
          Tax Mapping is most relevant when Invoice tax export mode is set to Canada / Non-US mapped QBO tax codes. You can still review mappings here at any time.
        </Alert>
      ) : null}

      {taxMappingErrors.length > 0 ? (
        <Alert severity="error">
          {taxMappingErrors.map((item, idx) => (
            <Typography key={`tax-error-${idx}`} variant="caption" display="block">{item.message || String(item)}</Typography>
          ))}
        </Alert>
      ) : null}
      {taxMappingWarnings.length > 0 ? (
        <Alert severity="warning">
          {taxMappingWarnings.map((item, idx) => (
            <Typography key={`tax-warning-${idx}`} variant="caption" display="block">{item.message || String(item)}</Typography>
          ))}
        </Alert>
      ) : null}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Elite Tax</TableCell>
            <TableCell>Country/State</TableCell>
            <TableCell>Rate</TableCell>
            <TableCell>QBO Sales Tax Code</TableCell>
            <TableCell>QBO Purchase Tax Code</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {eliteTaxes.length ? eliteTaxes.map((tax) => {
            const key = String(tax.taxId);
            const mapping = taxMapByTaxId[key] || {};
            const statusMeta = statusForTaxRow(tax);
            return (
              <TableRow key={key}>
                <TableCell>
                  <Typography variant="body2">{tax.type || tax.canonicalKey} - {rateLabel(tax.rate)}</Typography>
                  <Typography variant="caption" color="text.secondary">{tax.label || tax.canonicalKey}</Typography>
                </TableCell>
                <TableCell>{[tax.countryCode, tax.stateCode].filter(Boolean).join("/") || "-"}</TableCell>
                <TableCell>{rateLabel(tax.rate)}</TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  <FormControl fullWidth size="small">
                    <Select
                      displayEmpty
                      value={mapping.qboSalesTaxCodeRef || ""}
                      onChange={(e) => {
                        const selected = qboTaxCodes.find((code) => String(code.id) === String(e.target.value));
                        updateTaxMapRow(tax, {
                          qboSalesTaxCodeRef: e.target.value,
                          qboSalesTaxCodeName: selected?.name || (e.target.value === "NON" ? "NON" : e.target.value === "TAX" ? "TAX" : ""),
                        });
                      }}
                    >
                      <MenuItem value=""><em>Not mapped</em></MenuItem>
                      <MenuItem value="TAX">TAX - legacy taxable</MenuItem>
                      <MenuItem value="NON">NON - legacy non-taxable/exempt</MenuItem>
                      {qboTaxCodes.map((code) => (
                        <MenuItem key={`sales-${code.id}`} value={String(code.id)}>{qboTaxCodeLabel(code)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  <FormControl fullWidth size="small">
                    <Select
                      displayEmpty
                      value={mapping.qboPurchaseTaxCodeRef || ""}
                      onChange={(e) => {
                        const selected = qboTaxCodes.find((code) => String(code.id) === String(e.target.value));
                        updateTaxMapRow(tax, {
                          qboPurchaseTaxCodeRef: e.target.value,
                          qboPurchaseTaxCodeName: selected?.name || (e.target.value === "NON" ? "NON" : e.target.value === "TAX" ? "TAX" : ""),
                        });
                      }}
                    >
                      <MenuItem value=""><em>Not mapped</em></MenuItem>
                      <MenuItem value="TAX">TAX - legacy taxable</MenuItem>
                      <MenuItem value="NON">NON - legacy non-taxable/exempt</MenuItem>
                      {qboTaxCodes.map((code) => (
                        <MenuItem key={`purchase-${code.id}`} value={String(code.id)}>{qboTaxCodeLabel(code)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell><Chip size="small" label={statusMeta.label} color={statusMeta.color} /></TableCell>
              </TableRow>
            );
          }) : (
            <TableRow>
              <TableCell colSpan={6}>{loadingTaxMapping ? "Loading taxes..." : "No Elite taxes found for this instance."}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
        <Button variant="outlined" onClick={() => loadTaxMappingSource()} disabled={loadingTaxMapping || loading}>
          {loadingTaxMapping ? "Refreshing..." : "Refresh Tax Codes"}
        </Button>
        <Button variant="outlined" onClick={autoMatchTaxMapping} disabled={savingTaxMapping || loadingTaxMapping}>
          Auto-match
        </Button>
        <Button variant="outlined" onClick={validateTaxMapping} disabled={savingTaxMapping || loadingTaxMapping}>
          Validate
        </Button>
        <Button variant="contained" onClick={saveTaxMapping} disabled={savingTaxMapping || loadingTaxMapping}>
          {savingTaxMapping ? "Saving..." : "Save Tax Mapping"}
        </Button>
      </Box>
    </Box>
  );

  const renderAdvancedTab = () => (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box>
        <Typography variant="h6">Advanced / Diagnostics</Typography>
        <Typography variant="body2" color="text.secondary">
          Most users should use the Export Defaults and Tax Mapping tabs. This view is for manual tax-map JSON edits and raw QBO reference diagnostics.
        </Typography>
      </Box>

      {referenceWarnings.length > 0 ? (
        <Alert severity="warning">
          {referenceWarnings.map((message, idx) => (
            <Typography key={`advanced-reference-warning-${idx}`} variant="caption" display="block">{message}</Typography>
          ))}
        </Alert>
      ) : (
        <Alert severity="info">No current reference warnings.</Alert>
      )}

      <TextField
        label="Tax Map (JSON)"
        value={form.tax_map}
        onChange={setField("tax_map")}
        multiline
        minRows={10}
        fullWidth
      />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Reference Realm</Typography>
          <Typography variant="body2" color="text.secondary">Connected env: {referenceData.env || "unknown"}</Typography>
          <Typography variant="body2" color="text.secondary">Connected realm: {referenceData.realmId || "unknown"}</Typography>
          <Typography variant="body2" color="text.secondary">Saved reference env: {referenceData.savedReferenceEnv || "unknown"}</Typography>
          <Typography variant="body2" color="text.secondary">Saved reference realm: {referenceData.savedReferenceRealmId || "unknown"}</Typography>
          <Typography variant="body2" color="text.secondary">Realm mismatch: {referenceData.realmMismatch ? "Yes" : "No"}</Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Tax Diagnostics</Typography>
          <Typography variant="body2" color="text.secondary">Elite taxes loaded: {eliteTaxes.length}</Typography>
          <Typography variant="body2" color="text.secondary">QBO tax codes loaded: {qboTaxCodes.length}</Typography>
          <Typography variant="body2" color="text.secondary">Tax warnings: {taxMappingWarnings.length}</Typography>
          <Typography variant="body2" color="text.secondary">Tax errors: {taxMappingErrors.length}</Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={saveAdvancedTaxJson} disabled={savingAdvancedJson}>
          {savingAdvancedJson ? "Saving..." : "Save Advanced JSON"}
        </Button>
      </Box>
    </Box>
  );

  useEffect(() => {
    loadStatus();
    loadSettings();
    loadReferenceData();
    loadTaxMappingSource();
    loadFinanceCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  return (
    <Card elevation={3} sx={{ borderRadius: 2 }}>
      <CardHeader title="QuickBooks Online" />
      <Divider />
      <CardContent>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
          <Chip size="small" color={status.connected ? "success" : "default"} label={status.connected ? "Connected" : "Not connected"} />
          <Typography variant="body2" color="text.secondary">{statusSummary}</Typography>
        </Box>

        <Tabs
          value={activeQboTab}
          onChange={(_, value) => setActiveQboTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          <Tab value={QBO_TABS.connection} label="Connection" />
          <Tab value={QBO_TABS.defaults} label="Export Defaults" />
          <Tab value={QBO_TABS.financeCodes} label="Finance Code Mapping" />
          <Tab value={QBO_TABS.taxMapping} label="Tax Mapping" />
          <Tab value={QBO_TABS.advanced} label="Advanced / Diagnostics" />
        </Tabs>

        {activeQboTab === QBO_TABS.connection ? renderConnectionTab() : null}
        {activeQboTab === QBO_TABS.defaults ? renderExportDefaultsTab() : null}
        {activeQboTab === QBO_TABS.financeCodes ? renderFinanceCodeMappingTab() : null}
        {activeQboTab === QBO_TABS.taxMapping ? renderTaxMappingTab() : null}
        {activeQboTab === QBO_TABS.advanced ? renderAdvancedTab() : null}
      </CardContent>

      <Dialog
        open={resetConfirmOpen}
        onClose={resettingQbo ? undefined : () => setResetConfirmOpen(false)}
        aria-labelledby="qbo-reset-dialog-title"
        aria-describedby="qbo-reset-dialog-description"
      >
        <DialogTitle id="qbo-reset-dialog-title">Reset QuickBooks Online?</DialogTitle>
        <DialogContent>
          <DialogContentText id="qbo-reset-dialog-description" component="div">
            <Typography variant="body2" sx={{ mb: 1 }}>
              This will reset QuickBooks for the selected {String(form.qbo_env || "sandbox").toUpperCase()} environment.
            </Typography>
            <Box component="ul" sx={{ mt: 0, mb: 0, pl: 3 }}>
              <li>Disconnect the current QuickBooks connection for this environment.</li>
              <li>Clear saved QBO account, item, and tax mapping options.</li>
              <li>Not delete Elite invoices.</li>
              <li>Require reconnecting and remapping before exports work again.</li>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirmOpen(false)} disabled={resettingQbo}>Cancel</Button>
          <Button onClick={resetQboIntegration} color="warning" variant="contained" disabled={resettingQbo}>
            {resettingQbo ? "Resetting..." : "Yes, reset QuickBooks"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
